import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2, Edit3, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { SERVICE_CATEGORIES, getServiceCategoryLabel, getServiceCategoryColor } from './LeadCRM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const currency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

const LineItemEstimateDetail = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/line-item-estimates/${estimateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEstimate(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [estimateId]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this estimate? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.delete(
        `${BACKEND_URL}/api/line-item-estimates/${estimateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estimate deleted');
      navigate('/dashboard/estimates');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" /> Loading estimate...
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-600">Estimate not found.</p>
        <Link to="/dashboard/estimates" className="text-blue-600 underline">Back to Estimates</Link>
      </div>
    );
  }

  const catLabel = getServiceCategoryLabel(estimate.service_category);
  const catColor = getServiceCategoryColor(estimate.service_category);
  const catIcon = SERVICE_CATEGORIES.find(s => s.id === estimate.service_category)?.icon || '🏠';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="line-item-estimate-detail">
      <div className="flex items-center justify-between mb-6">
        <Link to="/dashboard/estimates">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Estimates
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => toast.info('Edit flow coming soon — for now, duplicate from a new estimate.')}
            data-testid="lie-edit-btn"
          >
            <Edit3 className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={handleDelete}
            data-testid="lie-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Header card */}
      <div className="bg-gradient-to-br from-blue-600 to-orange-500 rounded-2xl text-white shadow-xl p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className={`inline-block text-xs font-medium px-2 py-1 rounded-full bg-white/20 text-white mb-3`}>
              {catIcon} {catLabel}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              {estimate.customer_first_name || ''} {estimate.customer_last_name || 'Customer'}
            </h1>
            <p className="opacity-90">{estimate.scope_of_work || 'Home service job estimate'}</p>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-80 mb-1">Total</div>
            <div className="text-4xl font-extrabold" data-testid="lie-detail-total">{currency(estimate.total)}</div>
            <div className="mt-1 text-xs bg-white/20 inline-block px-2 py-0.5 rounded-full capitalize">{estimate.status}</div>
          </div>
        </div>
      </div>

      {/* Customer / Property */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Customer</h3>
          <div className="space-y-2 text-sm text-gray-700">
            {estimate.customer_email && (
              <div className="flex items-center"><Mail className="w-4 h-4 mr-2 text-blue-600" />{estimate.customer_email}</div>
            )}
            {estimate.customer_phone && (
              <div className="flex items-center"><Phone className="w-4 h-4 mr-2 text-blue-600" />{estimate.customer_phone}</div>
            )}
            {!estimate.customer_email && !estimate.customer_phone && (
              <p className="text-gray-500 italic">No contact info on file</p>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Property</h3>
          <div className="text-sm text-gray-700 flex items-start">
            <MapPin className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>{estimate.property_address || <span className="italic text-gray-500">No address provided</span>}</span>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Line Items</h3>
        {(!estimate.line_items || estimate.line_items.length === 0) ? (
          <p className="text-gray-500 italic">No line items</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lie-items-table">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3 text-right">Qty</th>
                  <th className="py-2 pr-3">Unit</th>
                  <th className="py-2 pr-3 text-right">Unit Price</th>
                  <th className="py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {estimate.line_items.map((li, i) => {
                  const sub = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
                  return (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-3 text-gray-900">{li.description || <span className="italic text-gray-400">Untitled</span>}</td>
                      <td className="py-2 pr-3 text-right">{li.quantity}</td>
                      <td className="py-2 pr-3">{li.unit || 'ea'}</td>
                      <td className="py-2 pr-3 text-right">{currency(li.unit_price)}</td>
                      <td className="py-2 text-right font-semibold">{currency(sub)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Totals breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
        <h3 className="font-semibold text-gray-900 mb-4">Pricing Breakdown</h3>
        <div className="grid grid-cols-2 gap-y-2 max-w-md ml-auto text-sm">
          <div className="text-gray-600">Materials / services</div>
          <div className="text-right">{currency(estimate.materials_subtotal)}</div>
          <div className="text-gray-600">Labor ({estimate.labor_hours}h × {currency(estimate.labor_rate)})</div>
          <div className="text-right">{currency(estimate.labor_subtotal)}</div>
          <div className="text-gray-600 border-t border-gray-200 pt-1">Subtotal</div>
          <div className="text-right font-semibold border-t border-gray-200 pt-1">{currency(estimate.subtotal)}</div>
          {estimate.discount_amount > 0 && (
            <>
              <div className="text-gray-600">Discount ({estimate.discount_percent}%)</div>
              <div className="text-right text-green-600">-{currency(estimate.discount_amount)}</div>
            </>
          )}
          {estimate.tax_amount > 0 && (
            <>
              <div className="text-gray-600">Tax ({estimate.tax_rate}%)</div>
              <div className="text-right">{currency(estimate.tax_amount)}</div>
            </>
          )}
          <div className="text-gray-900 font-bold text-lg border-t-2 border-gray-300 pt-2">Total</div>
          <div className="text-right text-gray-900 font-extrabold text-lg border-t-2 border-gray-300 pt-2">{currency(estimate.total)}</div>
        </div>
      </div>
    </div>
  );
};

export default LineItemEstimateDetail;
